/**
 * F20 (Wave 2D) — admin payment-marks history panel.
 *
 * Composes Card + AuditLogTimeline. Maps each `PaymentMark` row to the shared
 * `AuditEvent` shape via `paymentMarkToAuditEvent` (lives in a sibling file
 * to keep this module under `react-refresh/only-export-components`).
 *
 * Read-only across every admin role — the admin booking detail page renders
 * this below the existing lifecycle timeline. Empty state renders when the
 * `recentPaymentMarks` array is empty (the typical case for an unpaid
 * booking with no marks yet).
 */
import { useI18n } from '@bravemove/i18n/react';
import { AuditLogTimeline, Card, EmptyState } from '@bravemove/ui-web';
import type { PaymentMark } from '@bravemove/types';
import { paymentMarkToAuditEvent } from './paymentMarkMappers';

export interface PaymentMarksHistoryPanelProps {
  readonly marks: readonly PaymentMark[];
  /** Override the panel title — defaults to the i18n key. */
  readonly title?: string;
}

export function PaymentMarksHistoryPanel({ marks, title }: PaymentMarksHistoryPanelProps) {
  const { t } = useI18n();
  const resolvedTitle = title ?? t('payment:history.title', { defaultValue: 'Payment history' });

  return (
    <Card>
      <div className="flex flex-col gap-3" data-testid="payment-marks-history-panel">
        <h2 className="text-h2 font-bold text-ink">{resolvedTitle}</h2>
        {marks.length === 0 ? (
          <EmptyState
            title={t('payment:history.empty_title', {
              defaultValue: 'No payment activity yet',
            })}
            description={t('payment:history.empty_body', {
              defaultValue: 'Payment marks recorded for this booking will appear here.',
            })}
          />
        ) : (
          <div data-testid="payment-marks-history-list">
            <AuditLogTimeline events={marks.map(paymentMarkToAuditEvent)} />
          </div>
        )}
      </div>
    </Card>
  );
}
